import React from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";

interface ModernScheduleHeaderProps {
  displayedDate: Date;
  viewMode: "daily" | "weekly";
  onViewModeChange: (mode: "daily" | "weekly") => void;
}

const ModernScheduleHeader: React.FC<ModernScheduleHeaderProps> = ({
  displayedDate,
  viewMode,
  onViewModeChange,
}) => {
  const formatDate = () => {
    if (viewMode === "weekly") {
      const startOfWeek = new Date(displayedDate);
      startOfWeek.setDate(displayedDate.getDate() - displayedDate.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);

      return `${startOfWeek.toLocaleDateString("pt-BR", { day: "numeric", month: "short" })} - ${endOfWeek.toLocaleDateString("pt-BR", { day: "numeric", month: "short", year: "numeric" })}`;
    }

    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    };
    return displayedDate.toLocaleDateString("pt-BR", options);
  };

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: "daily" | "weekly" | null,
  ) => {
    if (newMode !== null) {
      onViewModeChange(newMode);
    }
  };

  return (
    <Box
      sx={{
        background: "#226FE2",
        color: "white",
        padding: { xs: "16px 20px", md: "20px 24px" },
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderRadius: "12px 12px 0 0",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      <Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.1rem", md: "1.5rem" },
            letterSpacing: "-0.5px",
            textShadow: "0 1px 2px rgba(0,0,0,0.1)",
          }}
        >
          {formatDate()}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            opacity: 0.9,
            fontSize: "0.875rem",
            fontWeight: 400,
            marginTop: "4px",
            display: "block",
          }}
        >
          Visualização de Agendamentos
        </Typography>
      </Box>

      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={handleViewModeChange}
        aria-label="view mode"
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          borderRadius: "8px",
          "& .MuiToggleButton-root": {
            color: "rgba(255, 255, 255, 0.7)",
            border: "none",
            padding: { xs: "6px 12px", md: "8px 16px" },
            fontSize: { xs: "0.75rem", md: "0.875rem" },
            fontWeight: 600,
            textTransform: "none",
            "&.Mui-selected": {
              backgroundColor: "rgba(255, 255, 255, 0.25)",
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.3)",
              },
            },
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
          },
        }}
      >
        <ToggleButton value="daily" aria-label="daily view">
          <CalendarTodayIcon
            sx={{ fontSize: { xs: "1rem", md: "1.2rem" }, mr: 0.5 }}
          />
          Diário
        </ToggleButton>
        <ToggleButton value="weekly" aria-label="weekly view">
          <ViewWeekIcon
            sx={{ fontSize: { xs: "1rem", md: "1.2rem" }, mr: 0.5 }}
          />
          Semanal
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

export default ModernScheduleHeader;
